/**
 * Grupos de supervisoras en la pantalla Solicitudes (spec
 * 2026-09-28-grupos-supervisoras §7). Módulo puro, sin React ni Firestore.
 *
 * Un grupo es `supervisor_groups/{uidSupervisora}` con `members` (uid de sus
 * contadoras), `available` y `backupUid`. El correo lo enruta la función
 * (daysi-cloud-functions/deletion_requests_mail.js#routeRecipients); aquí
 * solo se decide qué ve cada una con el filtro "Mi grupo" y qué se deja guardar.
 */

export const MAX_MEMBERS = 50;

const roleKind = (role) => {
  const d = role?.documents || {};
  if (d.root === true) return 'root';
  if (d.supervisor === true) return 'supervisor';
  return 'other';
};

/** Supervisoras, privilegiadas (supervisoras + root) y candidatas a miembro. */
export const classifyUsers = (users, rolesById) => {
  const supervisors = [];
  const privileged = new Set();
  const candidates = [];
  (Array.isArray(users) ? users : []).forEach((u) => {
    if (!u || !u.uid) return;
    const kind = roleKind(rolesById?.[u.rolId]);
    // La supervisora de empresa (Alcance 'empresas', spec 2026-10-05) no es
    // de los grupos globales: privilegiada (no se puede meter a un grupo),
    // pero tampoco cuenta como supervisora vigente al enrutar.
    if (kind === 'supervisor' && u.alcance !== 'empresas') supervisors.push(u);
    if (kind !== 'other') privileged.add(u.uid);
    else candidates.push(u);
  });
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es');
  return { supervisors: supervisors.sort(byName), privileged, candidates: candidates.sort(byName) };
};

// ¿Este uid es supervisora vigente? Sin lista (supervisorUids) no se comprueba.
const isSup = (uid, supervisorUids) => !(supervisorUids instanceof Set) || supervisorUids.has(uid);
const isOff = (uid, groups) => (groups || []).some((g) => g && g.uid === uid && g.available === false);

/**
 * La suplente que de verdad recibe el aviso de un grupo no disponible, o null
 * si no hay una válida (entonces lo cubren todas las demás disponibles).
 * Misma regla que routeRecipients en la función: supervisora vigente, no ella
 * misma y disponible.
 */
export const effectiveBackup = ({ group, groups, supervisorUids }) => {
  const b = group?.backupUid;
  if (!b || b === group.uid || !isSup(b, supervisorUids) || isOff(b, groups)) return null;
  return b;
};

/**
 * Las de su grupo más las de las compañeras no disponibles que ella cubre:
 * las que la tienen como suplente válida y, si la suplente no es válida o no
 * hay, las que cubren todas las demás. Una supervisora no disponible no cubre
 * a nadie (el correo tampoco le llega). Solo cuentan grupos de supervisoras
 * vigentes (supervisorUids), igual que en la función.
 */
export const myGroupMembers = ({ myUid, groups, supervisorUids }) => {
  const out = new Set();
  const list = Array.isArray(groups) ? groups : [];
  // Quien no es supervisora (p. ej. root) no recibe avisos por grupo.
  if (!isSup(myUid, supervisorUids)) return out;
  const meOff = isOff(myUid, list);
  list.forEach((g) => {
    if (!g || !Array.isArray(g.members) || !isSup(g.uid, supervisorUids)) return;
    let take = g.uid === myUid;
    if (!take && !meOff && g.available === false) {
      const backup = effectiveBackup({ group: g, groups: list, supervisorUids });
      take = backup ? backup === myUid : true;
    }
    if (take) g.members.forEach((m) => out.add(m));
  });
  return out;
};

export const inMyGroup = (request, members) => !!request && members instanceof Set && members.has(request.requestedBy);

export const isUnassigned = (request, groups, supervisorUids) => !!request
  && !(Array.isArray(groups) ? groups : []).some((g) => Array.isArray(g?.members)
    && isSup(g.uid, supervisorUids) && g.members.includes(request.requestedBy));

/**
 * Con qué grupo arranca la pestaña "Mi grupo": el suyo si es supervisora; si
 * es root sin ese rol, el de la primera supervisora (la función no le enruta
 * nada a un "grupo" de root); null si no hay supervisoras (client).
 */
export const initialGroupSid = ({ myUid, supervisors }) => {
  const list = Array.isArray(supervisors) ? supervisors : [];
  if (list.some((s) => s.uid === myUid)) return myUid;
  return list[0]?.uid || null;
};

export const defaultScope = (members) => (members instanceof Set && members.size > 0 ? 'mine' : 'all');

/** Mensaje de error si el grupo no se puede guardar, o null. */
export const validateGroup = ({ sid, members, backupUid, privileged }) => {
  const list = Array.isArray(members) ? members : [];
  if (backupUid && (backupUid === sid || !(privileged instanceof Set) || !privileged.has(backupUid))) {
    return 'La suplente tiene que ser otra supervisora.';
  }
  if (privileged instanceof Set && list.some((m) => privileged.has(m))) {
    return 'Una supervisora o un usuario root no puede estar en un grupo.';
  }
  if (new Set(list).size > MAX_MEMBERS) return 'Un grupo puede tener hasta 50 personas.';
  return null;
};
